import React from 'react';
import './PressPage.css';
import Seo from "./components/Seo";

const PressPage = () => {
    return (
        <div className="press-page">
            <Seo
                title="Press & Media — NYX NoCode"
                description="Press mentions, NPR features, logos, and media assets for NYX NoCode. Get the story behind student-made AI creativity."
            />
            <header className="press-header">
                <h1>Press Releases</h1>
            </header>

            <section className="press-section">
                <h2>Press Resources at a Glance</h2>
                <p>
                    Welcome to the NYX NoCode press center. Here you'll find official press releases, company information, and media resources covering our educational technology partnerships and AI-powered no-code platform that's revolutionizing how students learn to create digital solutions.
                </p>
                
                <div className="modes-grid">
                    <div className="mode-section">
                        <div className="mode-icon" aria-hidden="true">🚀</div>
                        <div className="mode-content">
                            <h3>About NYX NoCode</h3>
                            <p>An innovative AI-powered no-code platform that transforms prompts and doodles into functional web applications, making digital creation accessible to students and educators.</p>
                            <span className="mode-tag">Platform</span>
                        </div>
                    </div>

                    <div className="mode-section">
                        <div className="mode-icon" aria-hidden="true">👩‍💻</div>
                        <div className="mode-content">
                            <h3>About Karen Kilroy</h3>
                            <p>Six-time IBM Champion, AI engineer, and author of four O'Reilly books on AI and technology. Creator of NYX NoCode and recognized expert in making emerging technologies accessible.</p>
                            <span className="mode-tag">Founder</span>
                        </div>
                    </div>
                </div>

                <p className="glance-next">For media inquiries, interviews, or additional resources, please contact Karen Kilroy at <a href="mailto:info@nyx.baby">info@nyx.baby</a>.</p>
            </section>

            <article className="press-release">
                <div className="press-tag-line">
                    <span className="mode-tag">Press Release</span>
                    <a href="/Mena_School_District_NYX_NoCode_Hackathon_Press_Release_2025.pdf" download="Mena_School_District_NYX_NoCode_Hackathon_Press_Release_2025.pdf" className="download-button">
                        📄 Download PDF
                    </a>
                </div>
                <div className="press-release-header">
                    <h3>FOR IMMEDIATE RELEASE</h3>
                    <h2 className="press-title-normal">NYX NoCode Hackathon Returns to Mena School District</h2>
                    <p className="press-subtitle">Computer Science Students in Middle and High School Learn to Develop Software using Artificial Intelligence</p>
                    <p className="press-dateline"><strong>Mena, Arkansas</strong> – Fall/Winter, 2025</p>
                </div>

                <div className="press-content">
                    <p>
                        This year's NYX NoCode Hackathon invites students to design creative, no-code solutions that amplify
                        Bearcat pride — making it easier for students, families, and community members to discover and engage
                        with the full range of clubs, teams, and activities in the Mena School District.
                    </p>

                    <p>
                        In 2024, students impressed the community by transforming handwritten doodles into fully functional web
                        apps and interactive games without traditional coding. The 200 or so students formed teams and created
                        pages to promote the Trails at Mena. The entries from last year can be seen here:{' '}
                        <a href="https://nyx.baby/NYX-Catalog" target="_blank" rel="noopener noreferrer">https://nyx.baby/NYX-Catalog</a>
                    </p>

                    <p>
                        Teachers are at the heart of every NYX NoCode Hackathon — guiding students not only through the mechanics
                        of app design, but through the process of collaboration, critique, and growth. At Mena, computer science
                        educators Krystal Titsworth and Sarah Billings are creating space for students to engage meaningfully with
                        AI tools and take pride in what they build.
                    </p>

                    <blockquote>
                        "During the NYX NoCode Hackathon, our students were given the opportunity to transform their original ideas
                        into interactive websites that come to life in just minutes! Along the way, they've strengthened their soft
                        skills such as collaboration, communication, and perseverance which will benefit them long after they leave
                        my classroom. In reality, they are building much more than websites. They're building futures!" said{' '}
                        <strong>Krystal Titsworth</strong>, Mena High School computer science teacher.
                    </blockquote>

                    <p>
                        The hackathon includes hands-on experience with AI-generated imagery and video, immersive design tools, and
                        behind-the-scenes lessons in prompt engineering. Students walk away not just with digital projects they can
                        show — but with foundational skills in AI fluency, problem solving, critique of AI output and responsible
                        digital citizenship. Following the main Mena School District hackathon, there will be a demo hackathon
                        offered to special education students where less verbal participants can make and modify AI-generated images
                        using a piano keyboard.
                    </p>

                    <blockquote>
                        "Our students are learning how to think creatively and communicate clearly with technology. The NYX NoCode
                        Hackathon challenges them to design interactive websites using only their ideas and descriptive prompts.
                        There is no coding required. It's incredible to watch them discover that innovation isn't limited by age
                        or programming experience," said <strong>Sarah Billings</strong>, Mena Middle School computer science teacher.
                    </blockquote>

                    <p>
                        As superintendent, Dr. Lee Smith has championed opportunities that help Mena students build real-world digital
                        skills. His support for the NYX NoCode Hackathon reflects the district's broader commitment to preparing
                        students for a rapidly changing technological landscape. Last year, he saw firsthand how the event energized
                        classrooms and elevated student confidence.
                    </p>

                    <blockquote>
                        "The NYX NoCode Hackathon strengthens exactly what we want for our students — confidence, creativity, and
                        real-world skill. When students can take an idea, describe it clearly, and watch it become a working product
                        in minutes, they begin to see themselves as creators with a purpose. This event highlights the talent within
                        our schools and shows how innovative thinking is becoming a natural part of the Mena student experience,"
                        said <strong>Dr. Lee Smith</strong>, Superintendent of Mena School District.
                    </blockquote>

                    <p>
                        Along with expanded features for 2025 such as 3D rendering and video, NYX NoCode now teaches students to
                        "X-Ray" digital files for Content Credentials using C2PA.org metadata, deepening their awareness of
                        authenticity, authorship, and digital provenance.
                    </p>

                    <blockquote>
                        "In a confusing world of AI-generated content it is more important than ever that young people understand
                        C2PA provenance technologies in order to make better informed decisions about the origins of the content
                        they consume," said <strong>Santiago Lyon</strong>, Head of Advocacy & Education for Content Authenticity Initiative.
                    </blockquote>

                    <p>
                        This type of discernment is important in the fast-changing world of technology, which includes artificial
                        voices, deepfake video, and spatial computing. Sometimes it is hard to tell what is real.
                    </p>

                    <blockquote>
                        "Using NYX NoCode with Content Credentials, the teachers are equipped to help the students examine AI output
                        with a critical eye, helping them learn to evaluate its authenticity and effectiveness," said{' '}
                        <strong>Karen Kilroy</strong>, creator of NYX NoCode.
                    </blockquote>

                    <p>
                        NYX NoCode is currently seeking 2025–2026 sponsors for prizes for the Mena winning teams, as well as volunteer
                        judges to review and evaluate the entries. NYX NoCode also seeks funding to expand these in-classroom hackathons
                        to additional rural and underrepresented schools. Past supporters have included technology leaders, media outlets,
                        and local employers excited to engage with students building the future right from their desks.
                    </p>

                    <div className="press-contact">
                        <p><strong>Media Contact:</strong><br />
                        Karen Kilroy<br />
                        <a href="mailto:info@nyx.baby">info@nyx.baby</a></p>
                    </div>

                    <div className="about-highlights">
                        <div className="about-district">
                            <h3>About Mena School District</h3>
                            <p>
                                Mena School District is a public school district in Mena, Arkansas, serving students in Polk County.
                                The district has a mission to instill purpose, create community, and strengthen skills, and its values
                                include service, accountability, and relationships. Recent recognitions include being named the best
                                school system in Polk County by the Mena Star newspaper, and its alternative education program has
                                received state recognition for student outcomes.
                            </p>
                        </div>
                        <div className="about-nyx">
                            <h3>About NYX NoCode</h3>
                            <p>
                                NYX NoCode was founded by educator and technologist Karen Kilroy, author of <em>Blockchain Tethered AI</em>{' '}
                                (O'Reilly 2023), who brings decades of experience building inclusive, ethically grounded AI tools for
                                learning. NYX NoCode is an AI-powered, teacher-centered learning platform that lets students build
                                websites, games, and interactive media using natural language — no programming required. Designed for
                                classrooms, hackathons, and after-school programs, NYX NoCode introduces students to AI fluency, content
                                credentials, and digital creativity. It is AI-agnostic and designed to help every student become a
                                future-ready creator.
                            </p>
                        </div>
                        <div className="about-cai">
                            <h3>About Content Authenticity Initiative</h3>
                            <p>
                                The Content Authenticity Initiative (CAI) is an Adobe-led community of over 5,000 media and technology
                                partners working to implement an open-source industry standard (Coalition for Content Provenance and
                                Authenticity, or C2PA) for digital media provenance. Santiago Lyon, Head of Advocacy & Education for CAI,
                                is an award-winning war photographer and senior media executive, now a leading media provenance expert
                                dedicated to advancing transparency and trust in digital content. Karen Kilroy participates in C2PA as
                                AI/ML Task Force Co-Chair.
                            </p>
                        </div>
                    </div>
                </div>
            </article>

            <section className="press-archive">
                <h2>Past Press Releases</h2>
                <p><a href="/press/archive/2024">Mena School District and Local Tech Startup partner together for an innovative NoCode Hackathon</a></p>
            </section>
        </div>
    );
};

export default PressPage;